import { useEffect, useState } from "preact/hooks";
import { t } from "../i18n";
import { Icon } from "../icon";
import * as S from "../state";
import { Avatar, duration, IconButton } from "../widgets";
import { KEYS } from "./dialer";

function useNow(): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  return now;
}

export function CallsPanel() {
  const live = S.calls.value;
  const conf = S.conference.value;
  const established = live.filter((c) => c.state === "established").length;
  const inConf = live.filter((c) => conf.includes(c.call));
  const others = live.filter((c) => !conf.includes(c.call));
  return (
    <aside class="calls-panel" aria-label={t("calls.title")}>
      <header>
        <h2>{t("calls.title")}</h2>
        {established >= 2 && inConf.length < established && (
          <button type="button" class="btn soft" onClick={() => void S.merge()}>
            <Icon name="merge" size={18} /> {t("calls.merge")}
          </button>
        )}
      </header>
      {inConf.length > 0 && (
        <>
          <div class="conf-head">
            <span>{t("calls.conference", inConf.length)}</span>
            <button type="button" class="btn danger" onClick={() => void S.endConference()}>
              {t("calls.end_conference")}
            </button>
          </div>
          {inConf.map((c) => (
            <CallCard key={c.call} c={c} inConference />
          ))}
        </>
      )}
      {others.map((c) => (
        <CallCard key={c.call} c={c} conferenceExists={inConf.length > 0} />
      ))}
    </aside>
  );
}

function stateText(c: S.UiCall): string {
  if (c.state === "ended") return c.ended_reason ?? t("end.ended");
  if (c.state === "incoming") return t("call.incoming");
  if (c.state === "outgoing") return t("call.calling");
  if (c.state === "ringing" || c.state === "early") return t("call.ringing");
  if (c.on_hold) return t("call.on_hold");
  if (c.remote_hold) return t("call.remote_hold");
  return "";
}

function CallCard({ c, inConference, conferenceExists }: { c: S.UiCall; inConference?: boolean; conferenceExists?: boolean }) {
  const now = useNow();
  const [view, setView] = useState<"" | "keypad" | "transfer" | "stats">("");
  const [to, setTo] = useState("");
  const name = S.nameFor(c.peer, c.peer_name || undefined);
  const number = S.numberOf(c.peer);
  const est = c.state === "established";
  const [since] = useState(() => now - (c.duration ?? 0) * 1000);
  const keypadOpen = view === "keypad";
  useEffect(() => {
    if (keypadOpen) S.dtmfCall.value = c.call;
    else if (S.dtmfCall.value === c.call) S.dtmfCall.value = "";
  }, [keypadOpen]);
  const focus = est && !c.on_hold;
  const st = stateText(c);
  return (
    <section class={`call-card ${focus ? "focus" : ""}`} data-call={c.call} data-state={c.state} aria-label={name}>
      <div class="who">
        <Avatar name={name} />
        <div class="grow" style={{ minWidth: 0 }}>
          <div class="peer">{name}</div>
          <div class={`state ${c.on_hold ? "hold" : ""}`}>
            {name !== number && <span>{number} · </span>}
            {st || (est ? duration((now - since) / 1000) : "")}
          </div>
        </div>
      </div>
      {c.state === "incoming" ? (
        <div class="answer-row">
          <button type="button" class="btn danger" onClick={() => void S.decline(c.call)}>
            <Icon name="hangup" size={18} /> {t("call.decline")}
          </button>
          <button type="button" class="btn success" onClick={() => void S.answer(c.call)}>
            <Icon name="phone" size={18} /> {t("call.answer")}
          </button>
        </div>
      ) : c.state === "ended" ? null : (
        <>
          <div class="actions">
            <IconButton icon={c.muted ? "mic-off" : "mic"} label={c.muted ? t("call.unmute") : t("call.mute")} pressed={!!c.muted} disabled={!est} onClick={() => void S.mute(c.call, !c.muted)} />
            {!inConference && (
              <IconButton icon={c.on_hold ? "play" : "pause"} label={c.on_hold ? t("call.resume") : t("call.hold")} pressed={!!c.on_hold} disabled={!est} onClick={() => void S.hold(c.call, !c.on_hold)} />
            )}
            <IconButton icon="keypad" label={t("call.keypad")} pressed={keypadOpen} disabled={!est} onClick={() => setView(keypadOpen ? "" : "keypad")} />
            {!inConference && <IconButton icon="transfer" label={t("call.transfer")} pressed={view === "transfer"} disabled={!est} onClick={() => setView(view === "transfer" ? "" : "transfer")} />}
            {inConference && <IconButton icon="split" label={t("call.split")} onClick={() => void S.split(c.call)} />}
            {!inConference && conferenceExists && est && <IconButton icon="merge" label={t("call.join")} onClick={() => void S.join(c.call)} />}
            <IconButton icon="stats" label={t("call.stats")} pressed={view === "stats"} disabled={!est} onClick={() => setView(view === "stats" ? "" : "stats")} />
            <IconButton icon="hangup" label={t("call.hangup")} kind="danger" onClick={() => void S.hangup(c.call)} />
          </div>
          {keypadOpen && (
            <div class="mini-keypad" role="group" aria-label={t("call.keypad")}>
              {KEYS.map(([k]) => (
                <button type="button" onClick={() => void S.dtmf(c.call, k)}>
                  {k}
                </button>
              ))}
            </div>
          )}
          {view === "transfer" && (
            <form
              class="row"
              onSubmit={(e) => {
                e.preventDefault();
                void S.transfer(c.call, to);
                setView("");
              }}
            >
              <input type="tel" aria-label={t("call.transfer_to")} placeholder={t("call.transfer_to")} value={to} onInput={(e) => setTo((e.target as HTMLInputElement).value)} autoFocus />
              <button type="submit" class="btn primary" disabled={!to.trim()}>
                {t("call.transfer")}
              </button>
              <button
                type="button"
                class="btn"
                data-testid="transfer-voicemail"
                disabled={!/^\d{1,32}$/.test(to.trim())}
                onClick={() => {
                  void S.transferToVoicemail(c.call, to);
                  setView("");
                }}
              >
                <Icon name="voicemail" size={18} /> {t("call.to_voicemail", to.trim() || "…")}
              </button>
            </form>
          )}
          {view === "stats" && <Stats s={c.stats} />}
        </>
      )}
    </section>
  );
}

function Stats({ s }: { s?: Record<string, unknown> }) {
  if (!s) return <p class="muted">…</p>;
  const n = (k: string) => Number(s[k] ?? 0);
  const mos = n("mos");
  const verdict = mos >= 4.2 ? "excellent" : mos >= 3.8 ? "good" : mos >= 3.3 ? "fair" : "poor";
  return (
    <dl class="stats">
      <dt>{t("stats.quality")}</dt>
      <dd>{t(`diag.${verdict}`)}</dd>
      <dt>{t("stats.mos")}</dt>
      <dd>{mos.toFixed(2)}</dd>
      <dt>{t("stats.loss")}</dt>
      <dd>{n("loss_pct").toFixed(1)} %</dd>
      <dt>{t("stats.jitter")}</dt>
      <dd>{n("rx_jitter_ms")} ms</dd>
      <dt>{t("stats.rtt")}</dt>
      <dd>{n("rtt_ms")} ms</dd>
      <dt>{t("stats.bitrate")}</dt>
      <dd>
        {n("rx_kbps").toFixed(0)} / {n("tx_kbps").toFixed(0)} kb/s
      </dd>
      <dt>{t("stats.codec")}</dt>
      <dd>{String(s.codec ?? "—")}</dd>
      <dt>{t("stats.encryption")}</dt>
      <dd>{s.secure ? t("stats.encrypted") : t("stats.not_encrypted")}</dd>
    </dl>
  );
}
